// Pont « Fabriquer » : transforme une FORMULE cosmétique (snapshot du formulateur) en
// BROUILLON de fiche de fabrication cosmétique pré-remplie :
//   - fige la formule (snapshot JSON, immuable ensuite) ;
//   - pré-remplit les lignes « matières premières » avec leur PHASE (libellé lisible),
//     l'INCI connu et la quantité théorique en grammes (% -> g via la conversion canonique
//     quantiteGrammes du socle C1), modifiable à la pesée ;
//   - propose un nom de produit (le nom de la formule) et le poids théorique du batch.
// Le numéro de lot reste à saisir (unique par compte), comme côté savon.
//
// Analogue exact du savon (src/pages/calculateur/versFiche.ts). Module PUR (aucun React)
// pour rester testable sous vitest.

import { PHASES_ORDRE, quantiteGrammes, type PhaseId } from '@shared/cosmetique'
import type { LigneFicheCosmetiqueInput } from '@shared/schemas/fiche-cosmetique'
import type { FicheCosmetiqueDetail } from '@/lib/fiches-cosmetique'
import { enNombre } from './etat'
import { PHASE_LABEL } from './types'
import type { FormuleCosmetiqueSnapshot, LigneSaisie } from './types'

// Brouillon transmis à l'écran de création de fiche cosmétique (FicheCosmetiqueForm).
// `numeroLot` n'y figure pas : il reste saisi par l'utilisatrice (unicité par compte).
export interface BrouillonFicheCosmetique {
  // Nom de produit pré-rempli (nom de la formule d'origine). Modifiable.
  nomProduit?: string
  // Formule FIGÉE au moment de la fabrication (snapshot du formulateur, opaque côté serveur).
  snapshotFormule: Record<string, unknown>
  // Poids théorique du batch (g) = poids total de la formule.
  poidsTheoriqueG?: number
  // Lignes « matières premières » pré-remplies (phase, désignation, INCI, quantité théorique).
  lignes: LigneFicheCosmetiqueInput[]
}

// Arrondi à 1 décimale (grammes), cohérent avec l'affichage de la fiche.
const g = (n: number): number => Math.round(n * 10) / 10

// Construit le brouillon à partir du snapshot du formulateur et du nom de la formule.
export function construireBrouillonFicheCosmetique(
  snapshot: FormuleCosmetiqueSnapshot,
  nomFormule?: string,
): BrouillonFicheCosmetique {
  const poidsTotal = snapshot.poids_total
  const lignes: LigneFicheCosmetiqueInput[] = []
  let ordre = 1

  // On parcourt les phases dans l'ordre réglementaire d'affichage. Chaque ligne de saisie
  // (nom, INCI, % en texte) devient une ligne de fiche avec sa quantité théorique en grammes.
  for (const phase of PHASES_ORDRE as readonly PhaseId[]) {
    const lignesPhase: LigneSaisie[] = snapshot.lignes[phase] ?? []
    for (const l of lignesPhase) {
      const designation = l.nom.trim()
      // Une désignation vide échouerait à la validation (designation min 1) -> on l'ignore.
      if (designation === '') continue
      const qte = quantiteGrammes(poidsTotal, enNombre(l.pct))
      lignes.push({
        ordre: ordre++,
        phase: PHASE_LABEL[phase],
        designation,
        inci: l.inci ?? undefined,
        qtePeseeG: g(qte),
        // Aucun article catalogue déduit depuis une formule : lien laissé à la fiche.
        fait: false,
      })
    }
  }

  return {
    nomProduit: nomFormule?.trim() || undefined,
    // Snapshot figé (copie superficielle pour ne pas partager la référence de l'état React).
    snapshotFormule: { ...snapshot },
    poidsTheoriqueG: g(poidsTotal),
    lignes,
  }
}

// Construit un BROUILLON à partir d'une fiche cosmétique EXISTANTE, pour la DUPLIQUER (relancer
// une fabrication identique). On RECOPIE : la formule figée (snapshot, poids théorique), le nom
// du produit et les lignes « matières premières » (phase, désignation, INCI, fournisseur, article
// lié au stock, quantité théorique). On NE recopie PAS : le numéro de lot (unique par compte, à
// ressaisir), le statut (la nouvelle fiche démarre 'en_cours'), le process (bilan / emballages),
// les observations, ni le lot fournisseur / la DDM (propres au lot d'origine). Module PUR.
export function brouillonDepuisFicheCosmetique(fiche: FicheCosmetiqueDetail): BrouillonFicheCosmetique {
  return {
    nomProduit: fiche.nomProduit ?? undefined,
    snapshotFormule: fiche.snapshotFormule,
    poidsTheoriqueG: fiche.poidsTheoriqueG ?? undefined,
    lignes: fiche.lignes.map((l, i) => ({
      ordre: l.ordre ?? i + 1,
      phase: l.phase ?? undefined,
      designation: l.designation,
      inci: l.inci ?? undefined,
      fournisseur: l.fournisseur ?? undefined,
      articleId: l.articleId ?? undefined,
      qtePeseeG: l.qtePeseeG ?? undefined,
      fait: false,
    })),
  }
}
